import { useEffect, useState } from "react";
import { Award, Linkedin, Twitter, Link2, ExternalLink } from "lucide-react";
import {
  linkedInAddToProfileUrl,
  twitterIntentUrl,
  type ShareChannel,
} from "@epicredible/shared";
import { api, PUBLIC_BASE_URL, type WalletCredential } from "../lib/api.js";
import { Card, CardContent } from "../components/ui/card.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";

interface ObShape {
  issuer?: { name?: string };
  credentialSubject?: { achievement?: { name?: string } };
}

function readCredential(ob: unknown): { name: string; issuer: string } {
  const o = (ob ?? {}) as ObShape;
  return {
    name: o.credentialSubject?.achievement?.name ?? "Credential",
    issuer: o.issuer?.name ?? "Issuer",
  };
}

/** Recipient wallet (REQ-1.10): claimed credentials + one-click sharing (1.11). */
export function WalletPage() {
  const [credentials, setCredentials] = useState<WalletCredential[] | null>(null);

  useEffect(() => {
    void api.getWallet().then((r) => setCredentials(r.credentials));
  }, []);

  if (!credentials) {
    return <div className="h-40 animate-pulse rounded-[var(--radius-lg)] bg-secondary" />;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">My wallet</h1>
      {credentials.length === 0 ? (
        <Card>
          <CardContent className="grid place-items-center gap-2 p-12 text-center">
            <Award size={32} className="text-muted-foreground" />
            <p className="font-medium">No credentials yet</p>
            <p className="text-sm text-muted-foreground">
              When an issuer sends you a credential, claim it and it&apos;ll appear here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {credentials.map((c) => (
            <WalletCard key={c.publicId} credential={c} />
          ))}
        </div>
      )}
    </div>
  );
}

function WalletCard({ credential }: { credential: WalletCredential }) {
  const { name, issuer } = readCredential(credential.obJson);
  const verifyUrl = `${PUBLIC_BASE_URL}/id/${credential.publicId}`;
  const revoked = credential.status === "revoked";
  const info = { name, issuerName: issuer, verifyUrl, publicId: credential.publicId, issuedAt: credential.issuedAt };

  function share(channel: ShareChannel, url?: string) {
    void api.recordShare(credential.publicId, channel);
    if (url) window.open(url, "_blank", "noopener");
  }

  return (
    <Card>
      <CardContent className="flex h-full flex-col gap-3 p-5">
        <div className="flex items-start justify-between">
          <Award size={24} className="text-primary" />
          {revoked ? (
            <Badge variant="revoked">✕ Revoked</Badge>
          ) : (
            <Badge variant="verified">✓ Verified</Badge>
          )}
        </div>
        <div>
          <h3 className="font-semibold leading-tight">{name}</h3>
          <p className="text-sm text-muted-foreground">{issuer}</p>
        </div>
        <p className="text-xs text-muted-foreground">
          Issued {new Date(credential.issuedAt).toLocaleDateString()}
        </p>
        <div className="mt-auto flex flex-wrap gap-1.5 pt-2">
          <Button size="sm" variant="secondary" onClick={() => share("linkedin", linkedInAddToProfileUrl(info))}>
            <Linkedin size={14} /> LinkedIn
          </Button>
          <Button size="sm" variant="secondary" onClick={() => share("twitter", twitterIntentUrl(info))}>
            <Twitter size={14} />
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              void navigator.clipboard.writeText(verifyUrl);
              share("link");
            }}
          >
            <Link2 size={14} /> Copy
          </Button>
          <Button asChild size="sm" variant="ghost">
            <a href={verifyUrl} target="_blank" rel="noopener">
              <ExternalLink size={14} /> View
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
